import { ConvertedImageRef, mergeConvertedImages } from './ConvertedImageUtils';
import {
	NOTE_TYPE_LOG,
	NOTE_TYPE_INTERNAL_ATTACHMENT_LOG,
	NOTE_TYPE_SERVICE_CALL_CHECK_IN,
	NOTE_TYPE_SERVICE_CALL_CHECK_OUT,
} from '../constants/NoteTypes';

// Contiguous, same-author, image-only notes sent close together are grouped into a single
// "grid" row so attaching several photos reads as one action (WhatsApp-style) instead of one
// bubble per image. There is no backend change: the buyer API has no multi-photo note, so each
// image is still its own note under the hood — this only affects rendering + unread/deep-link
// accounting. Ported from the mobile buyer app (components/WorkOrderNotes.js).

export interface NoteReadDetail {
	readBy: string;
	[key: string]: unknown;
}

// A note as it flows through the notes surfaces. Only the fields grouping reads are named; the
// rest (createdAt, noteAddedByContact, s3EmailReferenceUrl, …) ride along via the index signature
// and the object spread so grouped rows stay drop-in compatible with the ungrouped ones.
export interface Note {
	id?: number;
	type?: string;
	noteAddedBy?: string;
	noteAddedAt?: string;
	photo?: string;
	video?: string;
	audio?: string;
	text?: string;
	elements?: unknown[];
	noteReadDetails?: NoteReadDetail[];
	/**
	 * Present only on grouped image rows: the `photo` of each merged note, in the order they were
	 * sent. A run of length 1 still carries a `photos` of length 1. Consumers render this as a grid.
	 */
	photos?: string[];
	/**
	 * Present only on grouped image rows: the `id` of every merged note, so a `?noteId=` deep-link
	 * to ANY image in the batch (not just the representative one) still resolves to this row.
	 */
	groupedNoteIds?: number[];
	convertedImages?: ConvertedImageRef[] | null;
	[key: string]: unknown;
}

// Single source of truth for parsing a note timestamp to epoch ms so the window comparison stays
// consistent with the rest of the notes pipeline.
const toEpochMs = (value: string | undefined): number =>
	value != null ? new Date(value).getTime() : NaN;

// Notes sent within this window of each other (and otherwise groupable) collapse into one grid.
export const GROUP_IMAGES_WINDOW_MS = 5 * 60 * 1000;

// A note that carries ONLY an image: no text/elements/video/audio, and not one of the log /
// service-call timeline entries (those render their own way and must never be swallowed into a
// grid). Mirrors the mobile isImageOnlyMessage.
export const isImageOnlyMessage = (note: Note): boolean =>
	!!note.photo &&
	!(note.text && note.text.length > 0) &&
	!(note.elements && note.elements.length > 0) &&
	!(note.video && note.video.length > 0) &&
	!(note.audio && note.audio.length > 0) &&
	note.type !== NOTE_TYPE_LOG &&
	note.type !== NOTE_TYPE_INTERNAL_ATTACHMENT_LOG &&
	note.type !== NOTE_TYPE_SERVICE_CALL_CHECK_IN &&
	note.type !== NOTE_TYPE_SERVICE_CALL_CHECK_OUT;

// Readers (by `readBy`) present in BOTH read-detail arrays, so a grouped grid counts as "read"
// only when every image in it is read — otherwise the grid should still surface as unread.
export const intersectReaders = (
	a: NoteReadDetail[] | undefined,
	b: NoteReadDetail[] | undefined
): NoteReadDetail[] => {
	const readers = new Set((b || []).map((detail) => detail.readBy));
	return (a || []).filter((detail) => readers.has(detail.readBy));
};

// Merge contiguous, same-author image-only notes sent within GROUP_IMAGES_WINDOW_MS into one row
// carrying a `photos` array. Expects ascending order (how the list is stored). The group aggregates
// read-state (intersection), adopts the most recent note's timestamp, and tracks every merged
// note's id in `groupedNoteIds` so a `?noteId=` deep-link to any image in the batch resolves to
// this row. A run of length 1 yields a `photos` of length 1.
export const groupContiguousImageNotes = (notes: Note[]): Note[] => {
	const grouped: Note[] = [];
	let currentGroup: Note | null = null;
	let lastImageTime = 0;

	for (const note of notes) {
		if (isImageOnlyMessage(note)) {
			const noteTime = toEpochMs(note.noteAddedAt);
			const withinWindow =
				Number.isFinite(noteTime) &&
				Number.isFinite(lastImageTime) &&
				Math.abs(noteTime - lastImageTime) <= GROUP_IMAGES_WINDOW_MS;

			if (currentGroup && currentGroup.noteAddedBy === note.noteAddedBy && withinWindow) {
				currentGroup.photos = [...(currentGroup.photos || []), note.photo as string];
				currentGroup.convertedImages = mergeConvertedImages(
					currentGroup.convertedImages,
					note.convertedImages
				);
				currentGroup.noteReadDetails = intersectReaders(
					currentGroup.noteReadDetails,
					note.noteReadDetails
				);
				currentGroup.noteAddedAt = note.noteAddedAt;
				currentGroup.id = note.id;
				if (note.id != null) {
					currentGroup.groupedNoteIds = [...(currentGroup.groupedNoteIds || []), note.id];
				}
			} else {
				currentGroup = {
					...note,
					photos: [note.photo as string],
					groupedNoteIds: note.id != null ? [note.id] : [],
				};
				grouped.push(currentGroup);
			}
			lastImageTime = noteTime;
		} else {
			currentGroup = null;
			lastImageTime = 0;
			grouped.push(note);
		}
	}

	return grouped;
};
